import React, { useState, useEffect, useMemo, useContext } from "react";
import { useParams } from "react-router";
import DataTable from "react-data-table-component";
import {
  Avatar,
  Button,
  TextField,
  Modal,
  Backdrop,
  Fade,
  Switch,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import Swal from "sweetalert2";

import Toast from "../../utils/Toast";

import Loader from "../../components/Loader";

import useAddons from "../../queries/addon/addons";
import useDeleteAddon from "../../mutation/addon/deleteAddon";
import useCreateAudit from "../../mutation/audit/createAudit";
import createAudit from "../../mutation/audit/createAuditNoHook";

import { AuthContext } from "../../utils/userProvider";
import editAddon from "../../mutation/addon/editAddonNoHook";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const Addons = (props) => {
  const [addonsResponse, reset] = useAddons();
  const [auditResponse, setAuditData] = useCreateAudit();
  const [tableData, setTableData] = useState([]);

  const { authState, setAuthState } = useContext(AuthContext);

  const [openDelete, setOpenDelete] = useState(false);

  const [filterText, setFilterText] = useState("");

  const [deleteRow, setDeleteRow] = useState();

  useEffect(() => {
    if (addonsResponse.data) {
      setTableData(addonsResponse.data);
    }
  }, [addonsResponse]);

  const newAddon = () => {
    window.location.href = "/dashboard/addons/new";
  };

  const editItem = (value) => {
    if (authState.editAccess) {
      window.location.href = "/dashboard/addons/edit/" + value.id;
    } else {
      window.location.href = "/dashboard/addons/view/" + value.id;
    }
  };

  const deleteItem = (value) => {
    setDeleteRow(value);
    setOpenDelete(true);
  };

  useEffect(() => {
    if (deleteRow) {
      setOpenDelete(true);
    }
  }, [deleteRow]);

  const handleMandatoryChange = (event, row) => {
    console.log(event.target.checked);
    let updateObject = {
      id: row.id,
      mandatory: event.target.checked,
    };
    let auditObject = {
      newData: updateObject,
      snapshotCurrent: row,
      currentData: row.id,
      type: "addon",
      action: "update",
    };

    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        auditObject.actionDescription = actionDescription;
        return auditObject;
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await createAudit(result.value, authState ? authState.jwt : "")
          .then((response) => {
            const Toast = Swal.mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
              didOpen: (toast) => {
                toast.addEventListener("mouseenter", Swal.stopTimer);
                toast.addEventListener("mouseleave", Swal.resumeTimer);
              },
            });
            Toast.fire({
              icon: "success",
              title: "Update Audit Created",
            });
            updateTableData(response.data);
          })
          .catch((err) => {
            Swal.fire({
              icon: "error",
              titleText: "Error",
              text: "An Error has occured. Please try again.",
              allowOutsideClick: true,
              allowEscapeKey: true,
            });
            updateTableData();
          });
      } else {
        updateTableData();
      }
    });
  };

  const updateTableData = (data) => {
    let newTableData = [...tableData];
    if (data != null) {
      let index = newTableData.findIndex((object) => object.id == data.id);
      newTableData[index] = data;
      setTableData(newTableData);
    } else {
      setTableData(newTableData);
    }
  };

  let columns = [
    {
      name: "Image",
      selector: "display_image",
      cell: (row, index, column, id) => {
        if (row["display_image"]) {
          return (
            <Avatar
              sx={{ width: 56, height: 56 }}
              alt={row.name}
              src={`${process.env.REACT_APP_BACKEND_URL}${row["display_image"].url}`}
            />
          );
        }
      },
    },
    {
      name: "Name",
      selector: "name",
      sortable: true,
    },
    {
      name: "Chinese Name",
      selector: "name_chinese",
      sortable: true,
    },
    {
      name: "Price (in AUD)",
      selector: "price",
      sortable: true,
    },
    {
      name: "Price HK",
      selector: "price_hk",
      sortable: true,
      cell: (row) => row.price_hk != null ? row.price_hk : "-",
    },
    {
      name: "Linked To Requirement",
      selector: "requirementName",
      cell: (row, index, column, id) => {
        if (row["requirementName"] && row["requirementId"]) {
          return (
            <span>
              {`${row["requirementName"]} (ID: ${row["requirementId"]})`}
            </span>
          );
        }
      },
    },
    {
      name: "Mandatory",
      sortable: false,
      cell: (row, index, column, id) => {
        if (authState.editAccess) {
          return (
            <Switch
              defaultChecked={row.mandatory}
              onChange={(event) => {
                handleMandatoryChange(event, row);
              }}
              disabled={!authState.editAccess}
              name="mandatory"
              inputProps={{ "aria-label": "secondary checkbox" }}
            />
          );
        } else {
          return (
            <span>
              {row.mandatory && "Yes"}
              {!row.mandatory && "No"}
            </span>
          );
        }
      },
    },
  ];

  columns.push({
    name: "Options",
    cell: (row, index, column, id) => {
      if (authState.editAccess) {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CreateIcon />}
              onClick={() => {
                editItem(row);
              }}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => {
                deleteItem(row);
              }}
            >
              Delete
            </Button>
          </div>
        );
      } else {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                editItem(row);
              }}
            >
              View
            </Button>
          </div>
        );
      }
    },
  });

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = tableData.filter(
    (item) =>
      (item.name &&
        item.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (item.price && item.price.toString().includes(filterText.toLowerCase())) ||
      (item.price_hk != null && item.price_hk.toString().includes(filterText.toLowerCase()))
  );

  const handleClose = () => {
    setOpenDelete(false);
  };

  const confirmDelete = () => {
    Swal.fire({
      title: "Create Updates",
      html: `<h4>Please provide a description before submitting your changes</h4>
      <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
      confirmButtonText: "Confirm",
      focusConfirm: false,
      preConfirm: () => {
        const actionDescription =
          Swal.getPopup().querySelector("#actionDescription").value;
        if (!actionDescription) {
          Swal.showValidationMessage(`Please enter description`);
        }
        return {
          actionDescription: actionDescription,
          snapshotCurrent: deleteRow,
          currentData: deleteRow.id,
          type: "addon",
          action: "delete",
        };
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setAuditData(result.value);
        console.error(result.value);
      }
    });
  };

  useEffect(() => {
    if (auditResponse.data) {
      Toast.fire({
        icon: "success",
        title: "Add-on deletion Audit Created",
      });
      setDeleteRow(null);
      setOpenDelete(false);
      reset();
    } else if (auditResponse.error) {
      Toast.fire({
        icon: "error",
        text: "An Error has occured. Please try again.",
      });
      setDeleteRow(null);
      setOpenDelete(false);
    }
  }, [auditResponse]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  const renderContent = () => {
    return (
      <div>
        <ListPageHeader
          title="Add-ons"
          actions={
            authState.role == "Executive" ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={newAddon}
              >
                Add
              </Button>
            ) : null
          }
        />
        <DataTable
          columns={columns}
          data={filteredItems}
          pagination
          noHeader={true}
          subHeader
          subHeaderComponent={subHeaderComponentMemo}
          customStyles={dataTableCustomStyles}
        />
      </div>
    );
  };

  return (
    <ListPageShell>
      <ListPageCard>
        {addonsResponse.loading && !addonsResponse.data && <Loader />}
        {!addonsResponse.loading && addonsResponse.data && renderContent()}
      </ListPageCard>
      <Modal
        aria-labelledby="delete-modal"
        aria-describedby="delete-modal-description"
        sx={modalSx}
        open={openDelete}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && auditResponse.loading) return;
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus={true}
      >
        <Fade in={openDelete}>
          <Box>
            <DeleteModalFrame title="Delete Add-on">
              {deleteRow && (
                <div>
                  <p id="delete-modal-description">
                    Are you sure you want to delete{" "}
                    <b>
                      <u>{deleteRow.name}</u>
                    </b>
                    ?
                  </p>
                  <p>
                    This action cannot be{" "}
                    <b>
                      <u>reversed</u>
                    </b>
                    .
                  </p>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button
                      onClick={confirmDelete}
                      variant="contained"
                      color="primary"
                      disabled={auditResponse.loading}
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="inherit"
                      disabled={auditResponse.loading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </div>
              )}
            </DeleteModalFrame>
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default Addons;
